import i18next from "i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { colors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { useReportingFinanceQuery } from "Reporting/Api/Queries/useReportingFinanceQuery";
import { ChartCard } from "./ChartCard";
import { ChartEmptyState } from "./ChartEmptyState";

const c = colors();

const getMonthShort = (month: number): string => {
  const date = new Date(2024, month - 1, 1);
  return new Intl.DateTimeFormat(i18next.language, { month: "short" }).format(
    date,
  );
};

const getMonthFull = (month: number, year: number): string => {
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(i18next.language, {
    month: "long",
    year: "numeric",
  }).format(date);
};

export const RevenueChart = (): React.ReactNode => {
  const { t } = useResource();
  const { data, isLoading } = useReportingFinanceQuery();

  const chartData = (data?.revenueOverTime ?? []).map((item) => ({
    shortLabel:
      item.month === 1
        ? `${getMonthShort(1)} ${item.year}`
        : getMonthShort(item.month),
    fullLabel: getMonthFull(item.month, item.year),
    amount: item.amount,
  }));

  const isEmpty = chartData.length === 0;

  return (
    <ChartCard
      title={t(Resources.Reporting.RevenueOverTime)}
      isLoading={isLoading}
      height={350}
    >
      {isEmpty ? (
        <ChartEmptyState />
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData}>
            <CartesianGrid stroke={c.Border} strokeDasharray="3 3" />
            <XAxis
              dataKey="shortLabel"
              tick={{ fill: c.TextSecondary, fontSize: 12 }}
              axisLine={{ stroke: c.Border }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: c.TextSecondary, fontSize: 12 }}
              axisLine={{ stroke: c.Border }}
              tickLine={false}
              tickFormatter={(v: number) =>
                formatCurrency(v, CurrencyCode.EUR, 0)
              }
            />
            <Tooltip
              contentStyle={{
                backgroundColor: c.BackgroundTile,
                border: `1px solid ${c.Border}`,
                borderRadius: 8,
              }}
              labelStyle={{ color: c.TextSecondary }}
              itemStyle={{ color: c.White }}
              labelFormatter={(_label, payload) =>
                payload?.[0]?.payload?.fullLabel ?? _label
              }
              formatter={(value) => [
                formatCurrency(Number(value), CurrencyCode.EUR, 0),
                t(Resources.Reporting.Revenue),
              ]}
              cursor={{ fill: "rgba(255, 255, 255, 0.05)" }}
            />
            <Bar
              dataKey="amount"
              fill={c.PrimaryMain}
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};
